import { getAuthRootUrl, getClassicApiEndpoint } from './../../constants/api'
import { ErrorWithContext, StoredUser, UserStatus, WtnConsents } from './../../types/global'
import auth from 'modules/auth'
import {
  composeErrorMessage,
  extractClassicErrorMessage,
  getOwnProfileUrl,
  getSignOutUrl,
  makeErrorWithContext,
} from 'utils'
import Storage from 'modules/storage'
import { ERROR_PAGE_FULL_URL, REL_PATHS, WtnPage } from 'constants/global'

const ACTIVATE_ITF_USER_ENDPOINT = getClassicApiEndpoint() + 'v2/User/ActivateItfUser'
const GET_CURRENT_USER_ENDPOINT = getClassicApiEndpoint() + 'v2/User/GetCurrentUser'
const CHANGE_USER_PASSWORD_ENDPOINT = getAuthRootUrl() + 'Account/ChangePassword'
const CHANGE_USER_EMAIL_ENDPOINT = getAuthRootUrl() + 'Account/ChangeEmail'

class UserApi {
  private static _allowedPagesForPendingUser: WtnPage[] = [
    'WALKUP_REG',
    'FASTTRACK_REG',
    'CONTACT',
    'TERMS_AND_CONDITIONS',
    'PRIVACY_POLICY',
  ]
  private static _allowedPathsForPendingUser = this._allowedPagesForPendingUser.map(
    page => REL_PATHS[page]
  )

  private static async generateCustomError(
    response: Response,
    baseMessage: string
  ): Promise<ErrorWithContext> {
    const classicErrorMessage = await extractClassicErrorMessage(response)
    const message = composeErrorMessage([baseMessage, classicErrorMessage])
    return makeErrorWithContext(response, { message })
  }

  static async activateUser(consents: WtnConsents): Promise<{ success: boolean }> {
    const res = await auth.fetch(ACTIVATE_ITF_USER_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(consents),
    })

    if (res.ok) {
      return await res.json()
    } else {
      const error = await this.generateCustomError(res, 'Failed to activate user')
      throw error
    }
  }

  static async canActivate(): Promise<boolean> {
    const res = await auth.fetch(`${ACTIVATE_ITF_USER_ENDPOINT}?simulate=true`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    })

    if (res.ok) {
      const data = await res.json()
      if (!data.success) console.warn(`User cannot activate: ${res.status} ${res.statusText}`)
      return data.success
    } else {
      const error = await this.generateCustomError(res, 'Failed to simulate user activation')
      throw error
    }
  }

  static async getCurrentUser(): Promise<{
    ID?: string
    Status?: number
    ExternalID?: string
    EmailAddress?: string
  }> {
    const res = await auth.fetch(GET_CURRENT_USER_ENDPOINT, {
      method: 'GET',
    })

    if (res.ok) {
      return await res.json()
    } else {
      const error = await this.generateCustomError(res, 'Failed to get current user')
      throw error
    }
  }

  static initChangePassword() {
    window.location.href =
      CHANGE_USER_PASSWORD_ENDPOINT + '?returnUrl=' + encodeURIComponent(window.location.href)
  }

  static initChangeEmail() {
    window.location.href =
      CHANGE_USER_EMAIL_ENDPOINT + '?returnUrl=' + encodeURIComponent(window.location.href)
  }

  static signOut(returnUrl?: string) {
    Storage.clearAllUserData()
    Storage.setAlertRequest('ALERT_LOGOUT')
    this.silentSignOut(returnUrl)
  }

  static silentSignOut(returnUrl?: string) {
    window.location.assign(getSignOutUrl(returnUrl || window.location.href))
  }

  private static async getAndSaveUserData() {
    const currentUser = await this.getCurrentUser()

    const userData: StoredUser = {
      userStatus: !currentUser.Status ? UserStatus.PENDING : UserStatus.ACTIVE,
      tennisId: currentUser.ExternalID || '',
      analyticsId: currentUser.ID || '',
    }

    Storage.saveUser(userData)

    return userData
  }

  static async getUserData() {
    let user = Storage.getUser()
    if (!user) user = await this.getAndSaveUserData()

    return user
  }

  static isPendingUserAllowed() {
    return !this._allowedPathsForPendingUser.every(
      path => window.location.href.indexOf(path) === -1
    )
  }

  static redirectToErrorPage() {
    window.location.assign(ERROR_PAGE_FULL_URL)
  }

  static redirectToOwnProfilePage() {
    window.location.assign(getOwnProfileUrl())
  }
}

export default UserApi
